import type { PortalProjectDetail } from '@altios/api-client';
import { customerTask, selectionComplete, taskDone } from './portalModel';

export type PortalAttention = ReturnType<typeof getPortalAttention>;

/** Only current, actionable customer work turns navigation red. */
export function getPortalAttention(detail: PortalProjectDetail, unreadMessages = 0, pendingEstimateNotice = false) {
  const currentContract = [...detail.contracts]
    .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0];

  // A customer can sign only when BOTH the contract is sent and its latest
  // immutable PDF signature request is still pending. A regeneration cancels it.
  const agreements = Boolean(currentContract &&
    String(currentContract.status || '').toLowerCase() === 'sent for signature' &&
    String(currentContract.signature_request_status || '').toLowerCase() === 'pending' &&
    currentContract.signature_document_id &&
    !currentContract.customer_signed_at);

  const changes = detail.changeOrders.filter((order) =>
    String(order.status || '').toLowerCase() === 'sent for signature' &&
    (!order.signature_request_status || String(order.signature_request_status).toLowerCase() === 'pending')
  ).length;

  const estimates = Boolean(detail.estimate?.canApprove || pendingEstimateNotice);
  const payments = Number(detail.financialSummary?.nextDueAmount || 0) > 0;
  const selections = detail.selections.filter((item) =>
    String(item.status || '').toLowerCase() !== 'removed' && !selectionComplete(item)
  ).length;
  const tasks = detail.tasks.filter((task) => customerTask(task) && !taskDone(task)).length;
  const appointments = detail.appointments.filter((appointment) =>
    String(appointment.status || '').toLowerCase() === 'proposed' &&
    String(appointment.proposed_by || '').toLowerCase() !== 'customer'
  ).length;

  // Layout approval currently lives in the homepage document viewer, not Files.
  const home = detail.layout?.status === 'Provided' && Boolean(detail.layout.document_id);
  const money = Number(estimates) + Number(agreements) + changes + Number(payments);

  return {
    home,
    selections,
    messages: unreadMessages > 0,
    schedule: tasks + appointments > 0,
    money: money > 0,
    moneyCount: money,
    moneySections: { estimates, agreements, changes, payments },
    changeCount: changes
  };
}
